import arrayDiffer from 'array-differ';

import {
    type Block,
    type Change,
    PARENT_TYPE_INVALID,
    Schema,
    type SchemaNormalizeFn,
} from '@gitbook/slate';
import { createOrderIndex } from '@gitbook/util-orderindex';

import { isHeading } from '../heading';
import {
    BLOCK_COLUMN,
    BLOCK_IF,
    BLOCK_LIST_ITEM,
    BLOCK_PARAGRAPH,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    type BlockTableData,
    FRAGMENT_EXPANDABLE_BODY,
    type TableView,
} from '../types';
import { setRecordValue } from './changes';
import { columnSpecs } from './columns';
import { viewSpecs } from './views';

/**
 * Table schema.
 */
export const schema = Schema.create({
    validations: [validateTable],
    blocks: {
        [BLOCK_TABLE]: {
            isVoid: true,
            parent: {
                objects: ['document', 'fragment', 'block'],
                types: [
                    undefined,
                    BLOCK_LIST_ITEM,
                    BLOCK_TABS_ITEM,
                    BLOCK_STEPPER_STEP,
                    FRAGMENT_EXPANDABLE_BODY,
                    BLOCK_IF,
                    BLOCK_COLUMN,
                    BLOCK_UPDATE,
                ],
            },
            data: {
                records: (records) => {
                    return typeof records === 'object';
                },
                definition: (definition) => {
                    return typeof definition === 'object';
                },
                view: (view) => {
                    return typeof view === 'object';
                },
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
            },
            normalize: (change, violation, context) => {
                switch (violation) {
                    case PARENT_TYPE_INVALID: {
                        const { node } = context;
                        // When table is in an invalid parent, we unwrap it until we reach a valid parent
                        // @ts-expect-error
                        return change.unwrapNodeByKey(node.key);
                    }
                }
            },
        },
    },
});

/**
 * Validate and normalize an entire table.
 */
function validateTable(node: Block): undefined | ((change: Change) => void) {
    if (node.object !== 'block' || node.type !== BLOCK_TABLE) {
        return;
    }

    const data = node.data.toFrozenObject() as BlockTableData;

    // If table has no records or definition, remove the entire table block
    if (!data.records || Object.keys(data.records).length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }
    if (!data.definition || Object.keys(data.definition).length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    const changeDefinition = validateDefinitions(node, data);
    if (changeDefinition) {
        return (change) => {
            change.withoutNormalization(changeDefinition);
        };
    }

    // Only validate records if there are records to validate
    if (Object.keys(data.records).length > 0) {
        const changeRecords = validateRecords(node, data);
        if (changeRecords) {
            return (change) => {
                change.withoutNormalization(changeRecords);
            };
        }
    }

    const changeView = validateView(node, data);
    if (changeView) {
        return (change) => {
            change.withoutNormalization(changeView);
        };
    }

    const changeBlockAlignment = validateBlockTextAlignment(node);
    if (changeBlockAlignment) {
        return (change) => {
            change.withoutNormalization(changeBlockAlignment);
        };
    }
}

/**
 * Validates the definition of the table.
 */
function validateDefinitions(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    const changes: SchemaNormalizeFn[] = [];

    const definitionEntries = Object.entries(data.definition);

    // Table should have at lease one definition
    if (definitionEntries.length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    definitionEntries.forEach(([colId, colDef]) => {
        const colValidation = columnSpecs[colDef.type];

        if (!colValidation) {
            changes.push((change) => {
                change.removeDataPathByKey(node.key, ['definition', colId]);
            });
            return;
        }

        if (!colValidation.normalizeDefinition) {
            return;
        }

        const result = colValidation.normalizeDefinition(colDef);
        if (typeof result !== 'undefined' && result !== colDef) {
            changes.push((change) => {
                if (result) {
                    change.setDataByKey(node.key, ['definition', colId], result);
                } else {
                    change.removeDataPathByKey(node.key, ['definition', colId]);
                }
            });
        }
    });

    return changes.length === 0
        ? undefined
        : (change) => {
              changes.forEach((callback) => {
                  callback(change);
              });
          };
}

/**
 * Validates the records of a table.
 */
function validateRecords(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    const recordEntries = Object.entries(data.records);

    const changes: SchemaNormalizeFn[] = [];

    /** Fragments that are expected in the table */
    const validFragmentKeys: string[] = [];

    /** Order index already allocated */
    const allocatedOrderIndexes: string[] = [];

    // Verify that all Records contain a valid column entry.
    const colIds = Object.keys(data.definition);

    recordEntries.forEach(([key, record]) => {
        // Records should have an orderable index
        if (typeof record.orderIndex !== 'string') {
            changes.push((change) => {
                change.insertDataByKey(
                    node.key,
                    ['records', key, 'orderIndex'],
                    createOrderIndex(
                        allocatedOrderIndexes.length > 0
                            ? { before: allocatedOrderIndexes[allocatedOrderIndexes.length - 1] }
                            : {}
                    ),
                    { normalize: false }
                );
            });
        } else {
            let indexToAllocate = record.orderIndex;

            // And it should be unique
            if (allocatedOrderIndexes.includes(record.orderIndex)) {
                indexToAllocate = createOrderIndex({
                    before: allocatedOrderIndexes[allocatedOrderIndexes.length - 1],
                });

                changes.push((change) => {
                    change.insertDataByKey(
                        node.key,
                        ['records', key, 'orderIndex'],
                        indexToAllocate,
                        { normalize: false }
                    );
                });
            }

            allocatedOrderIndexes.push(indexToAllocate);
        }

        if (record.values) {
            // Remove any record values that do not have a valid column definition.
            const unknownRecordValues = Object.keys(record.values).filter(
                (key) => !(key in data.definition)
            );

            if (unknownRecordValues.length > 0) {
                changes.push((change) => {
                    unknownRecordValues.forEach((recordValue) => {
                        change.removeDataPathByKey(node.key, [
                            'records',
                            key,
                            'values',
                            recordValue,
                        ]);
                    });
                });
            }
        }

        // Validate the column
        colIds.forEach((colId) => {
            const colDef = data.definition[colId];
            const value = record.values?.[colDef.id];
            const colValidation = columnSpecs[colDef.type];

            // Delegate to the column validation for validating/normalizing the value itself.
            const validatedValue = colValidation.normalize(key, value, colDef, node);

            if (validatedValue !== undefined && validatedValue !== value) {
                changes.push((change) => {
                    const result =
                        typeof validatedValue === 'function'
                            ? validatedValue(change)
                            : validatedValue;
                    if (typeof result !== 'undefined') {
                        setRecordValue(change, node.key, key, colDef.id, result);
                    }
                });
            } else {
                // Keep track of the valid fragment keys.
                if (colValidation.fragmentKeys) {
                    validFragmentKeys.push(...colValidation.fragmentKeys(key, value, colDef, node));
                }
            }
        });
    });

    const existingFragmentKeys = node.fragments.toKeysArray();

    // Remove any fragments not actively referenced by a record.
    const unknownFragments = arrayDiffer(existingFragmentKeys, validFragmentKeys);
    if (unknownFragments.length > 0) {
        changes.push((change) => {
            unknownFragments.forEach((fragmentKey) => {
                change.removeFragmentInParentByKey(node.key, fragmentKey);
            });
        });
    }

    // Assert fragments exist. They are created by the text column validation.
    const missingFragments = arrayDiffer(validFragmentKeys, existingFragmentKeys);
    if (missingFragments.length > 0) {
        throw new Error(`Normalization should have created ${missingFragments.length} fragments`);
    }

    return changes.length === 0
        ? undefined
        : (change) => {
              changes.forEach((callback) => {
                  callback(change);
              });
          };
}

/**
 * Validates the view of a Table.
 */
function validateView(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    if (!data.view) {
        return (change) => {
            const defaultView: TableView = {
                type: 'grid',
                columns: [],
                hideHeader: true,
                useNewSizing: true,
            };
            change.setDataByKey(node.key, ['view'], defaultView);
        };
    }

    const viewValidation = viewSpecs[data.view.type];

    const normalized = viewValidation ? viewValidation.normalize(data.view, data) : null;

    if (normalized === null) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    if (typeof normalized !== 'undefined' && normalized !== data.view) {
        return (change) => {
            change.setDataByKey(node.key, ['view'], normalized);
        };
    }

    return;
}

/**
 * Ensure paragraphs/headings within table fragments do not have alignment defined.
 */
function validateBlockTextAlignment(node: Block): undefined | SchemaNormalizeFn {
    const paragraphs = new Set<string>();

    node.fragments.forEach((fragment) => {
        fragment.nodes.forEach((block) => {
            if ((block.type === BLOCK_PARAGRAPH || isHeading(block)) && block.data.has('align')) {
                paragraphs.add(block.key);
            }
        });
    });

    return paragraphs.size === 0
        ? undefined
        : (change) => {
              paragraphs.forEach((key) => {
                  change.removeDataByKey(key, ['align']);
              });
          };
}
